/**
 * One space's tree, fetched.
 *
 * The panel a page drops in when it wants navigation: the node list, filtered
 * server-side by `permission_visible_nodes`, plus the review markers §7.2 asks
 * for. `node-tree.tsx` draws it; everything here is about where the two pieces
 * of data come from.
 *
 * **There is no per-node metadata on the tree endpoint.** `GET /api/spaces/:id/nodes`
 * returns `node` rows — id, type, title, owner, position — and nothing from the
 * `document` payload table, so the tree has no status and no review date of its
 * own to render. The one endpoint that answers "which documents are past their
 * date" in bulk is `GET /api/documents/review/due`, and that is what this uses.
 * If the API ever joins the document payload into the tree response, this hook
 * should be deleted and the fields read from the row.
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useReviewDue, useSpaceNodes } from '../../api/queries.ts';
import type { LapsedReview, TreeNode } from '../../api/types.ts';
import { ErrorState, Loading } from '../ui/feedback.tsx';
import { Card, CardHeader } from '../ui/layout.tsx';
import { NodeTree, type ReviewIndex } from './node-tree.tsx';

/**
 * Every document the viewer can see that is past its review date, by node id.
 *
 * `scope: 'all'` rather than `'mine'`, and the distinction is not the one
 * `queries.ts` warns about. That warning is about the *nag* — §7.2 nags the
 * owner, singular, because a nag that reaches everyone who could fix it is a
 * nag nobody owns. This is not a nag; it is a marker on somebody else's
 * document saying "this is stale, do not build on it", which is useless if it
 * only appears on the documents you already own.
 *
 * Nothing is disclosed by asking for the wider scope: the route intersects its
 * answer with `visibleNodeIds(document.view)` before it returns, so the ids
 * that come back are ids the tree was already about to draw.
 *
 * A failure here is swallowed. The markers are a decoration on navigation, and
 * blanking the tree because a decoration could not load is a worse outcome than
 * an unmarked stale document — which is still visible, still findable, and
 * still marked everywhere the document itself is rendered.
 */
export function useLapsedReviewIndex(): ReviewIndex {
  const query = useReviewDue('all');

  return useMemo(() => {
    const index = new Map<string, LapsedReview>();
    for (const document of query.data?.documents ?? []) index.set(document.nodeId, document);
    return index;
  }, [query.data]);
}

export interface SpaceTreeProps {
  spaceId: string;
  /**
   * Which capability the tree is measured against — the same knob the space
   * page exposes. `document.view` is the reading answer and the default; a
   * screen asking "what could I edit here" passes `document.edit` and gets a
   * smaller tree, because that is a different question with a different answer.
   */
  capability?: string;
  activeNodeId?: string;
  actions?: (node: TreeNode) => ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  headerActions?: ReactNode;
  /** Replaces the "nothing you can reach" copy when the page can say why. */
  emptyState?: ReactNode;
  /**
   * Rendered inside the card, under the tree. The space page reports the
   * outcome of a delete there; putting it outside the card would detach the
   * sentence from the list it is about.
   */
  footer?: ReactNode;
}

export function SpaceTree({
  spaceId,
  capability = 'document.view',
  activeNodeId,
  actions,
  title = 'Nodes',
  description,
  headerActions,
  emptyState,
  footer,
}: SpaceTreeProps) {
  const nodes = useSpaceNodes(spaceId, capability);
  const reviews = useLapsedReviewIndex();

  return (
    <Card padded={false}>
      <CardHeader
        title={title}
        description={
          description ??
          'Documents, files, and links in this space — filtered to what you can reach by the set-based query rather than by a per-row check.'
        }
        actions={headerActions}
      />
      {nodes.isPending ? (
        <Loading />
      ) : nodes.isError ? (
        <div className="p-5">
          <ErrorState error={nodes.error} />
        </div>
      ) : (
        <NodeTree
          nodes={nodes.data.nodes}
          activeNodeId={activeNodeId}
          reviews={reviews}
          actions={actions}
          emptyState={emptyState}
        />
      )}
      {footer}
    </Card>
  );
}
